import type { InteractivePrompt } from '@eriko/shared-types';
import { cn } from '@eriko/ui/src/lib/utils';
import { useChatStore } from '../../../stores/chatStore';

export function InteractivePromptCard({
  messageId,
  prompt,
  isLatest,
  onAutoSubmit,
}: {
  messageId: string;
  prompt: InteractivePrompt;
  isLatest: boolean;
  onAutoSubmit: () => void;
}) {
  const pending = useChatStore((s) => s.pendingAnswers);
  const setPending = useChatStore((s) => s.setPendingAnswer);

  const compactMode = Boolean(prompt.answers) || !isLatest;

  if (compactMode) {
    return (
      <div className="mt-3 space-y-1 text-sm">
        {prompt.rows.map((r) => {
          const a = prompt.answers?.[r.rowId];
          if (!a) {
            return (
              <div key={r.rowId} className="text-surface-400">
                — {r.subject.split(' — ')[0]} (skipped)
              </div>
            );
          }
          const opt = r.options.find((o) => o.slug === a.value);
          return (
            <div key={r.rowId} className="text-surface-700">
              ✓ {r.subject.split(' — ')[0]} → <span className="font-medium">{opt?.label ?? a.value}</span>
              {a.makeRule && <span className="text-surface-500 text-xs ml-1">(rule applied)</span>}
            </div>
          );
        })}
      </div>
    );
  }

  const answersForThis =
    pending && pending.messageId === messageId ? pending.answers : {};

  const isSingleQuestion = prompt.rows.length === 1;

  return (
    <div className="mt-3 space-y-4">
      {prompt.rows.map((r) => {
        const current = answersForThis[r.rowId];
        return (
          <div key={r.rowId} className="space-y-1.5">
            <div className="text-sm text-surface-700">{r.subject}</div>
            <div className="flex flex-wrap gap-1.5">
              {r.options.map((o) => {
                const selected = current?.value === o.slug;
                return (
                  <button
                    key={o.slug}
                    type="button"
                    onClick={() => {
                      setPending(
                        r.rowId,
                        { value: o.slug, makeRule: current?.makeRule ?? false },
                        messageId,
                      );
                      if (isSingleQuestion && !r.allowRule) {
                        onAutoSubmit();
                      }
                    }}
                    className={cn(
                      'px-3 py-1 rounded-full border text-[12px] font-medium transition-colors',
                      selected
                        ? 'bg-surface-900 border-surface-900 text-white'
                        : 'bg-surface-0 border-surface-300 text-surface-700 hover:border-surface-500',
                    )}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            {r.allowRule && current && (
              <div className="text-xs text-surface-500 flex gap-2 pt-0.5">
                <span>
                  Always map {r.subject.split(' — ')[0]} to{' '}
                  {r.options.find((o) => o.slug === current.value)?.label}?
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setPending(r.rowId, { ...current, makeRule: true }, messageId);
                    if (isSingleQuestion && r.allowRule) {
                      onAutoSubmit();
                    }
                  }}
                  className={cn(
                    'underline underline-offset-2',
                    current.makeRule ? 'text-surface-900 font-medium' : 'text-surface-500',
                  )}
                >
                  yes, make a rule
                </button>
                <span className="text-surface-300">/</span>
                <button
                  type="button"
                  onClick={() => {
                    setPending(r.rowId, { ...current, makeRule: false }, messageId);
                    if (isSingleQuestion && r.allowRule) {
                      onAutoSubmit();
                    }
                  }}
                  className={cn(
                    'underline underline-offset-2',
                    !current.makeRule ? 'text-surface-900 font-medium' : 'text-surface-500',
                  )}
                >
                  just this time
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
